import { useCallback } from "react";
import { useAppAuthState } from "../../hooks/useAppAuthState";
import { usePlaceNotes } from "../../hooks/usePlaceNotes";
import { useUser } from "../../hooks/useUser";
import { useUserProfile } from "../../hooks/useUserProfile";
import { Place } from "../../types/Place";
import { Headings } from "../Headings";
import { InlineLoadingAlert } from "../InlineLoadingAlert/InlineLoadingAlert";
import { NoteCard } from "./NoteCard";
import { NoteForm } from "./NoteForm";
import "./PlaceNotes.scss";

interface PlaceNotesProps {
  place: Place;
}

export const PlaceNotes: React.FC<PlaceNotesProps> = ({ place }) => {
  const { user: authUser, isAuthLoading } = useAppAuthState();
  const { user } = useUser(authUser?.uid);
  const { profile: userProfile } = useUserProfile(user?.uid);
  const {
    placeNotes,
    loading: isPlaceNotesLoading,
    refetch: refetchPlaceNotes,
  } = usePlaceNotes(place.googlePlaceId);

  const handleNoteAdd = useCallback(() => {
    refetchPlaceNotes();
  }, [refetchPlaceNotes]);

  return (
    <div className="place-notes-wrapper">
      <Headings.PhoneSubBox htmlId="notes">Notes</Headings.PhoneSubBox>
      <NoteForm
        place={place}
        profile={userProfile}
        disabled={isAuthLoading}
        onAddNote={handleNoteAdd}
      />
      <InlineLoadingAlert
        loading={isPlaceNotesLoading}
        message="Loading notes..."
      />
      {placeNotes.length === 0 && (
        <div className="place-notes-empty alert alert-warning">
          No notes yet. Be the first to add one!
        </div>
      )}
      {placeNotes.length > 0 && (
        <div className="place-notes-list">
          {placeNotes.map((note) => (
            <NoteCard key={note.id} note={note} user={user} />
          ))}
        </div>
      )}
    </div>
  );
};
